import { useMemo } from 'react';
import useSWR from 'swr';
import { CACHE_KEYS } from '@/constants/cache-keys';
import { EngineService } from '@/services/engine';
import { SourceType } from '@/types/source';
import { SortOrder } from '@/types/table';
import { CameraTableRow } from '../types';

interface UseCameraTableDataOptions {
    page: number;
    pageSize: number;
    search?: string;
    sortBy?: string;
    sortOrder?: SortOrder;
}

interface UseCameraTableDataReturn {
    cameras: CameraTableRow[];
    isLoading: boolean;
    totalCount: number;
    refetch: () => void;
}

export function useCameraTableData(
    options: UseCameraTableDataOptions,
): UseCameraTableDataReturn {
    const { page, pageSize, search, sortBy, sortOrder } = options;

    const orderBy = sortBy && sortOrder ? `${sortBy} ${sortOrder}` : undefined;

    const {
        data: camerasData,
        isLoading: isLoadingCameras,
        mutate,
    } = useSWR(
        [CACHE_KEYS.CAMERAS, page, pageSize, search, orderBy],
        () =>
            EngineService.getCameras({
                type: SourceType.CAMERA,
                page,
                size: pageSize,
                search: search || undefined,
                orderBy,
            }),
        {
            revalidateOnFocus: false,
        },
    );

    const cameraItems = camerasData?.items || [];
    const totalCount = camerasData?.page.totalItems || 0;

    const cameras = useMemo(() => {
        if (!cameraItems.length) return [];

        return cameraItems.map((camera) => ({
            id: camera.source.id,
            sourceId: camera.sourceId,
            name: camera.source.name,
            streamUrl: camera.streamUrl,
            username: camera.username || '',
            password: '******',
        }));
    }, [cameraItems]);

    return {
        cameras,
        isLoading: isLoadingCameras,
        totalCount,
        refetch: mutate,
    };
}
